import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, OnChanges, SimpleChanges } from "@angular/core";

/**
 * EVA Tooltip Directive - 100% Custom (sans PrimeNG)
 *
 * Directive attribut pour afficher un tooltip au survol.
 * Remplace pTooltip de PrimeNG.
 *
 * @example
 * // Simple
 * <button evaTooltip="Information utile">Survolez-moi</button>
 *
 * // Avec position
 * <button evaTooltip="En bas" evaTooltipPosition="bottom">...</button>
 *
 * // Avec délai
 * <button evaTooltip="Avec délai" [evaTooltipShowDelay]="500">...</button>
 */
@Directive({
  selector: "[evaTooltip]",
  standalone: true,
})
export class EvaTooltipDirective implements OnInit, OnDestroy, OnChanges {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Contenu du tooltip */
  @Input("evaTooltip") content: string = "";

  /** Position du tooltip */
  @Input() evaTooltipPosition: "top" | "right" | "bottom" | "left" = "top";

  /** Délai avant affichage (ms) */
  @Input() evaTooltipShowDelay: number = 200;

  /** Délai avant masquage (ms) */
  @Input() evaTooltipHideDelay: number = 0;

  /** Désactiver le tooltip */
  @Input() evaTooltipDisabled: boolean = false;

  /** Thème du tooltip */
  @Input() evaTooltipTheme: "dark" | "light" = "dark";

  /** Largeur maximale */
  @Input() evaTooltipMaxWidth: string = "300px";

  // ==========================================================================
  // State
  // ==========================================================================

  private tooltipElement: HTMLElement | null = null;
  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;
  private isVisible: boolean = false;
  private listeners: (() => void)[] = [];

  constructor(
    private el: ElementRef,
    private ngZone: NgZone,
  ) {}

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngOnChanges(changes: SimpleChanges): void {
    // Si le contenu change alors que le tooltip est visible, mettre à jour
    if (changes["content"] && this.isVisible && this.tooltipElement) {
      const span = this.tooltipElement.querySelector("span");
      if (span) {
        span.textContent = this.content;
      }
      this.positionTooltip();
    }
  }

  ngOnInit(): void {
    // Register listeners outside NgZone to avoid triggering change detection on hover
    this.ngZone.runOutsideAngular(() => {
      const el = this.el.nativeElement;
      const onEnter = () => this.scheduleShow();
      const onLeave = () => this.scheduleHide();
      const onClick = () => {
        this.clearTimeouts();
        this.hide();
      };

      el.addEventListener("mouseenter", onEnter);
      el.addEventListener("mouseleave", onLeave);
      el.addEventListener("focusin", onEnter);
      el.addEventListener("focusout", onLeave);
      el.addEventListener("click", onClick);

      this.listeners = [
        () => el.removeEventListener("mouseenter", onEnter),
        () => el.removeEventListener("mouseleave", onLeave),
        () => el.removeEventListener("focusin", onEnter),
        () => el.removeEventListener("focusout", onLeave),
        () => el.removeEventListener("click", onClick),
      ];
    });
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.removeTooltip();
    this.listeners.forEach((unsub) => unsub());
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  private scheduleShow(): void {
    if (this.evaTooltipDisabled || !this.content) return;

    this.clearTimeouts();
    this.showTimeout = setTimeout(() => {
      this.show();
    }, this.evaTooltipShowDelay);
  }

  private scheduleHide(): void {
    this.clearTimeouts();
    this.hideTimeout = setTimeout(() => {
      this.hide();
    }, this.evaTooltipHideDelay);
  }

  private show(): void {
    if (this.isVisible || !this.content) return;

    this.isVisible = true;
    this.createTooltip();
  }

  private hide(): void {
    if (!this.isVisible) return;

    this.isVisible = false;
    this.removeTooltip();
  }

  private createTooltip(): void {
    const el = document.createElement("div");
    el.className = `eva-tooltip-popup eva-tooltip-popup--${this.evaTooltipPosition} eva-tooltip-popup--${this.evaTooltipTheme}`;
    el.style.maxWidth = this.evaTooltipMaxWidth;
    // Ancrage initial en haut à gauche du document. Sans top/left, l'élément se place à sa
    // position statique en bas du flux : il allonge le body et fait apparaître une scrollbar
    // verticale qui rétrécit le viewport (~15px) et décale les hôtes alignés à droite.
    // getBoundingClientRect() lirait alors un host décalé → flèche/tooltip rabattus vers la gauche.
    el.style.top = "0";
    el.style.left = "0";

    const span = document.createElement("span");
    span.textContent = this.content;
    el.appendChild(span);

    document.body.appendChild(el);
    this.tooltipElement = el;

    this.positionTooltip();
  }

  private positionTooltip(): void {
    if (!this.tooltipElement) return;

    const hostRect = this.el.nativeElement.getBoundingClientRect();
    // offsetWidth/Height : dimensions de layout indépendantes du transform. getBoundingClientRect()
    // renverrait la taille réduite par l'animation scale(0.95) encore en cours → centrage faussé.
    const tooltipRect = {
      width: this.tooltipElement.offsetWidth,
      height: this.tooltipElement.offsetHeight,
    };
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    let top: number;
    let left: number;
    const gap = 8;

    switch (this.evaTooltipPosition) {
      case "top":
        top = hostRect.top + scrollTop - tooltipRect.height - gap;
        left = hostRect.left + scrollLeft + (hostRect.width - tooltipRect.width) / 2;
        break;
      case "bottom":
        top = hostRect.bottom + scrollTop + gap;
        left = hostRect.left + scrollLeft + (hostRect.width - tooltipRect.width) / 2;
        break;
      case "left":
        top = hostRect.top + scrollTop + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.left + scrollLeft - tooltipRect.width - gap;
        break;
      case "right":
        top = hostRect.top + scrollTop + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.right + scrollLeft + gap;
        break;
    }

    // Auto-flip
    const padding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let effectivePosition = this.evaTooltipPosition;

    if (effectivePosition === "top" && hostRect.top < tooltipRect.height + gap + padding) {
      effectivePosition = "bottom";
      top = hostRect.bottom + scrollTop + gap;
      this.tooltipElement.classList.replace("eva-tooltip-popup--top", "eva-tooltip-popup--bottom");
    } else if (
      effectivePosition === "bottom" &&
      viewportHeight - hostRect.bottom < tooltipRect.height + gap + padding
    ) {
      effectivePosition = "top";
      top = hostRect.top + scrollTop - tooltipRect.height - gap;
      this.tooltipElement.classList.replace("eva-tooltip-popup--bottom", "eva-tooltip-popup--top");
    } else if (effectivePosition === "left" && hostRect.left < tooltipRect.width + gap + padding) {
      effectivePosition = "right";
      left = hostRect.right + scrollLeft + gap;
      this.tooltipElement.classList.replace("eva-tooltip-popup--left", "eva-tooltip-popup--right");
    } else if (effectivePosition === "right" && viewportWidth - hostRect.right < tooltipRect.width + gap + padding) {
      effectivePosition = "left";
      left = hostRect.left + scrollLeft - tooltipRect.width - gap;
      this.tooltipElement.classList.replace("eva-tooltip-popup--right", "eva-tooltip-popup--left");
    }

    // Centre du host (pour positionner la flèche si le tooltip est décalé)
    const hostCenterX = hostRect.left + scrollLeft + hostRect.width / 2;
    const hostCenterY = hostRect.top + scrollTop + hostRect.height / 2;

    left = Math.max(padding, Math.min(left, viewportWidth - tooltipRect.width - padding + scrollLeft));
    top = Math.max(padding + scrollTop, Math.min(top, viewportHeight - tooltipRect.height - padding + scrollTop));

    // Ajuster la flèche pour qu'elle pointe vers le centre du host
    if (effectivePosition === "top" || effectivePosition === "bottom") {
      const arrowLeft = hostCenterX - left;
      const clampedArrow = Math.max(12, Math.min(arrowLeft, tooltipRect.width - 12));
      this.tooltipElement.style.setProperty("--arrow-left", `${clampedArrow}px`);
    } else {
      const arrowTop = hostCenterY - top;
      const clampedArrow = Math.max(12, Math.min(arrowTop, tooltipRect.height - 12));
      this.tooltipElement.style.setProperty("--arrow-top", `${clampedArrow}px`);
    }

    this.tooltipElement.style.top = `${top}px`;
    this.tooltipElement.style.left = `${left}px`;
  }

  private removeTooltip(): void {
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
  }

  private clearTimeouts(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
